import React from "react";
import { useTable, useSortBy, Row, Cell } from "react-table";
import InfiniteScroll from "react-infinite-scroll-component";
import { Loader } from "@mantine/core";
import { EFilterBy, TFilter } from "./Dashboard";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

declare type Fn = () => any;
type Props = {
  data: any;
  columns: any;
  nextToken: string | null;
  loadOrders: Fn;
  fetchLoading: boolean;
  filter?: TFilter;
  setOrderFilter?: (value: TFilter) => void;
};

const OrderTableInfiniteScroll: React.FC<Props> = ({
  data,
  columns,
  nextToken,
  loadOrders = () => {},
  fetchLoading,
  filter,
  setOrderFilter,
}) => {
  const reorderedColumns = React.useMemo(() => {
    const columnOrder = [
      "id",
      "status",
      "lastOpened",
      "productType",
      "internalNote",
      "printManufacturer",
      "deliveryDate",
      "shippingMethod",
      "manufacturerNote",
      "createdAt",
      "uploadedDate",
    ];
    // Erstelle eine Kopie des columns-Arrays, bevor du es sortierst
    return [...columns].sort(
      (a: { accessor: string }, b: { accessor: string }) =>
        columnOrder.indexOf(a.accessor) - columnOrder.indexOf(b.accessor),
    );
  }, [columns]);
  
  // Filter out duplicate data entries based on the 'id' field, with a null check for 'data'
  const uniqueData = React.useMemo(() => {
    if (!data) return []; // Return empty array if data is null or undefined
    const uniqueOrders = new Map();
    data.forEach((order: any) => {
      if (!uniqueOrders.has(order.id)) {
        uniqueOrders.set(order.id, order);
      }
    });
    return Array.from(uniqueOrders.values());
  }, [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: reorderedColumns,
      data: uniqueData,
    },
    useSortBy
  );

  return (
    <>
      <InfiniteScroll
        dataLength={rows.length}
        next={loadOrders}
        hasMore={Boolean(nextToken)}
        loader={<></>}
      >
        <div className="table-wrapper table-auto">
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup: any) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any) => {
                    // Überspringe die Spalte "viewOrder"
                    if (column.id !== "viewOrder") {
                      return (
                    <th
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        style: {
                          minWidth: column.minWidth,
                        },
                      })}
                    >
                      <div
                            className="row row-v-center row-center gap-4 white-space-no-wrap"
                        onClick={() => {
                          // remove sort type from other columns
                          if (
                            setOrderFilter &&
                            filter &&
                            filter.filterBy &&
                            Object.values(EFilterBy).includes(
                              filter.filterBy
                            ) &&
                            !Object.values(EFilterBy).includes(
                              column.sortValue
                            ) &&
                            filter.sortByOrder !== null
                          ) {
                            setOrderFilter({ ...filter, sortByOrder: null });
                          }
                        }}
                      >
                        {column.render("Header")}
                        <span style={{ marginTop: 7 }}>
                          {column.isSortedDesc
                            ? column.isSorted && <IoIosArrowUp size={30} />
                            : column.isSorted && <IoIosArrowDown size={30} />}
                        </span>
                      </div>
                    </th>
                      );
                    }
                    return null; // Wenn es sich um die "viewOrder"-Spalte handelt, überspringe sie
                  })}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row: Row<{ id?: string }>) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps({ key: row.original.id})}>
                    {row.cells.map((cell: Cell) => {
                      // Überspringe die Zellen für die "viewOrder"-Spalte
                      if (cell.column.id !== "viewOrder") {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                      );
                      }
                      return null;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </InfiniteScroll>
      {fetchLoading && (
        <div className="row row-center mt-20">
          <Loader />
        </div>
      )}
    </>
  );
};

export default OrderTableInfiniteScroll;
